import { useCallback, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { LuEllipsisVertical, LuPlus } from 'react-icons/lu'

import { GeofenceAlert } from '@/feature/observatory/types/GeoFence.types'
import { CheckboxToggle } from '@/components/CheckboxToggleInput'

import { Button } from '@/lib/core/components/Button'
import { Tooltip } from '@/lib/core/components/Tooltip'

import { GeoFenceAlertDialog } from './GeoFenceAlertDialog'
import { InfoTooltip } from './InfoTooltip'
import styles from './GeofenceAlertsField.module.css'

export const GeofenceAlertsField = ({
  geofenceAlertRules = [],
}: {
  geofenceAlertRules?: GeofenceAlert[]
}) => {
  const [alertDialogOpen, setAlertDialogOpen] = useState(false)
  const { append, remove } = useFieldArray({
    name: 'alerts',
  })
  const [selectedAlertRule, setSelectedAlertRule] = useState<GeofenceAlert | undefined>(undefined)
  const [alertRules, setAlertRules] = useState<GeofenceAlert[]>(geofenceAlertRules)

  const generateAlertRuleText = useCallback(
    (alertRule: GeofenceAlert) => {
      let text = ''
      if (alertRule.tags?.length) {
        text += `tagged assets`
      } else {
        text += `all assets`
      }
      if (alertRule.alertOnEnter) {
        text += `, enter`
      }
      if (alertRule.alertOnExit) {
        text += `, exit`
      }
      if (alertRule.dwellTime) {
        text += `, and exceed dwell time of ${alertRule.dwellTime} mins`
      }
      text += `. Send to contacts ${alertRule.emails?.join(', ')} ${alertRule.phoneNumbers?.join(', ')}.`
      return `Send when ${text}`
    },
    [alertRules],
  )

  const { getValues, register } = useFormContext()

  const alertRuleIndex = useMemo(() => {
    return selectedAlertRule
      ? alertRules.findIndex((alertRule) => alertRule.id === selectedAlertRule?.id)
      : alertRules.length
  }, [selectedAlertRule, alertRules])

  const onSaveAlertRule = (alertId: number | null) => {
    const alert = getValues(`alerts.${alertRuleIndex}`)

    const { true: sendAlertToPhoneNumbers = [], false: sendAlertToEmails = [] } = Object.groupBy(
      alert.contacts,
      (contact: string) => String(isValidPhoneNumber(contact)),
    )

    const alertRule = alertRules.find((alertRule) => alertRule.id === alertId)

    if (alertRule) {
      alertRule.title = alert.title
      alertRule.enabledAlert = alert.enabledAlert
      alertRule.alertOnEnter = alert.alertOnEnter
      alertRule.alertOnExit = alert.alertOnExit
      alertRule.emails = sendAlertToEmails
      alertRule.phoneNumbers = sendAlertToPhoneNumbers
      alertRule.tags = alert.tags
      alertRule.dwellTime = alert.dwellTime
    } else {
      setAlertRules((prevState: GeofenceAlert[]) => [
        ...prevState,
        {
          id: Date.now(),
          title: alert.title,
          enabledAlert: alert.enabledAlert,
          alertOnEnter: alert.alertOnEnter,
          alertOnExit: alert.alertOnExit,
          emails: sendAlertToEmails,
          phoneNumbers: sendAlertToPhoneNumbers,
          tags: alert.tags,
          dwellTime: alert.dwellTime,
        },
      ])
    }
    setAlertDialogOpen(false)
  }

  const onDelete = (index: number) => {
    remove(index)
    setAlertRules((prevState: GeofenceAlert[]) => prevState.filter((_, i) => i !== index))
  }

  const onDiscard = (index: number, onUpdate: boolean = false) => {
    if (!onUpdate) {
      remove(index)
    }
    setAlertDialogOpen(false)
  }

  return (
    <div className={styles.section}>
      <label className={styles.alertLabel}>
        Alerts
        <InfoTooltip placement="bottom">
          Configure and activate <strong>Alerts</strong> to receive email or WhatsApp notifications
          for events.
        </InfoTooltip>
      </label>

      <div className={styles.alertButtons}>
        <Button
          className={styles.addAlertButton}
          leftIcon={<LuPlus size="16" strokeWidth={1.5} />}
          onClick={() => {
            setSelectedAlertRule(undefined)
            append({
              enabledAlert: true,
              alertOnEnter: false,
              alertOnExit: false,
              alertOnDwell: false,
              dwellTime: 0,
              enableDwellTime: false,
              tags: [],
              contacts: [],
            })
            setAlertDialogOpen(true)
          }}
          size="medium"
          variant="ghost"
          data-testid="add-alert"
        >
          Add Alert
        </Button>
        {alertRules?.map((alertRule: GeofenceAlert, index: number) => (
          <div
            className={styles.alertRuleWrapper}
            key={`alert-rule-${index}`}
            data-testid="alert-rule-row"
          >
            <div className={styles.alertRuleHeader}>
              <div className={styles.alertRuleTitleIndex} data-testid="alert-rule-title">
                {alertRule.title}
              </div>
              <div>
                <CheckboxToggle {...register(`alerts.${index}.enabledAlert`)} />
              </div>
            </div>
            <div className={styles.alertRuleContent}>
              <Tooltip
                interactive
                trigger="mouseenter"
                arrowOffset={50}
                contentPadding={3}
                placement="top"
                tooltipContent={
                  <div className={styles.infoTooltip}>{generateAlertRuleText(alertRule)}</div>
                }
              >
                <div className={styles.alertRuleText} data-testid="alert-rule-summary">
                  {generateAlertRuleText(alertRule)}
                </div>
              </Tooltip>

              <div className={styles.actionsContainer}>
                <div
                  className={styles.actions}
                  onClick={(e) => e.stopPropagation()}
                  role="button"
                  aria-label={'more-options'}
                  tabIndex={0}
                  data-testid="alert-rule-options"
                >
                  <LuEllipsisVertical size={24} strokeWidth={1.8} />
                  <div className={styles.moreOptions}>
                    <div className={styles.moreOptionsList}>
                      {[
                        {
                          label: 'Edit',
                          testId: 'alert-rule-edit',
                          onClick: () => {
                            setSelectedAlertRule(alertRule)
                            setAlertDialogOpen(true)
                          },
                        },
                        {
                          label: 'Delete',
                          testId: 'alert-rule-delete',
                          onClick: () => onDelete(index),
                        },
                      ]?.map((v, i) => (
                        <div
                          className={styles.moreOptionsListItem}
                          onClick={v.onClick}
                          key={i}
                          data-testid={v.testId}
                        >
                          {v.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alertDialogOpen && (
        <GeoFenceAlertDialog
          open={alertDialogOpen}
          onClose={onDiscard}
          onSaveAlertRule={onSaveAlertRule}
          alertRule={selectedAlertRule}
          alertIndex={alertRuleIndex}
        />
      )}
    </div>
  )
}
